import { Box, Button, Checkbox, Divider, Flex, Spinner } from "@chakra-ui/react";
import { Modal, Icons, Typography, useCurrencyFormatter, SearchInput } from "@reconvert/react-ui-component";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useSearchProduct from "../../../../../hooks/useSearchProduct";
import { Platform, ProductData } from "../../../../../models";
import { useAppSelector } from "../../../../../app/hooks";
import { selectAppState } from "../../../../../app/slice/appSlice";
import { ConditionFact } from "@reconvert/reconvert-utils";
import { APP_PLATFORM } from "../../../../../config";
import { compressImage, compressImageForWix } from "../../../../../utils/helpers";
import { useDirection } from "../../../../../hooks/useDirection";

/** Optional product titles by productId, used when passing selection back (e.g. for MISSING_ENTITIES). */
export interface SearchProductModalSetValueOptions {
  productTitles?: Record<string, string>;
}

interface SearchProductModalProps {
  value: Record<string, string[]>; // <productId, variantId[]>
  setValue: (val: Record<string, string[]>, options?: SearchProductModalSetValueOptions) => void;
  handleOnClose: () => void;
  conditionFact?: string;
}

interface FooterProps {
  onSave: () => void;
  onCancel: () => void;
  t: any;
}

interface ModalContentProps {
  searchTerm: string;
  searching: boolean;
  setSearchTerm: (val: string) => void;
  products: ProductData[];
  selectedProductIdObj: Record<string, string[]>; // <productId, variantId[]>
  handleProductSelect: (productId: string, variantIds: string[], isChecked: boolean) => void;
  conditionFact?: string
}

const SearchProductModal: React.FC<SearchProductModalProps> = ({ value, setValue, handleOnClose, conditionFact }) => {
  const { t } = useTranslation();
  const { direction } = useDirection();
  const { searching, searchTerm, setSearchTerm, products, selectedProductIdObj, handleProductSelect } =
    useSearchProduct(value || {}, conditionFact || ConditionFact.SPECIFIC_PRODUCT);

  const handleProductSave = useCallback(() => {
    const productTitles: Record<string, string> = {};

    if (conditionFact === ConditionFact.SPECIFIC_PRODUCT && products?.length) {
      Object.keys(selectedProductIdObj).forEach((productId) => {
        const product = products.find((p) => p.id === productId);

        if (product?.title) productTitles[productId] = product.title;
      });
    }

    setValue(selectedProductIdObj, Object.keys(productTitles).length ? { productTitles } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductIdObj, products, conditionFact]);

  return (
    <Box>
      <Modal
        okButton={{
          text: t("Done"),
          onClick: handleProductSave,
          variant: "primary",
        }}
        cancelButton={{
          text: t("Cancel"),
          onClick: handleOnClose,
          variant: "secondary",
        }}
        size={"xl"}
        isOpen={true}
        onClose={handleOnClose}
        hideCloseButton
        direction={direction}
      >
        <ModalContent
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          products={products}
          selectedProductIdObj={selectedProductIdObj}
          handleProductSelect={handleProductSelect}
          searching={searching}
          conditionFact={conditionFact}
        />
      </Modal>
    </Box>
  );
};

const ModalContent: React.FC<ModalContentProps> = ({
  searching,
  searchTerm,
  setSearchTerm,
  products,
  selectedProductIdObj,
  handleProductSelect,
  conditionFact
}) => {
  const { t } = useTranslation();
  const { storeInfo } = useAppSelector(selectAppState);
  const { currencyFormatter } = useCurrencyFormatter();

  const imageSrc = useCallback((imageUrl: string) => {
    if (imageUrl) {
      return APP_PLATFORM === Platform.Shopify ? compressImage(imageUrl, 32) : compressImageForWix(imageUrl, 32);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compressImage, compressImageForWix]);

  return (
    <>
      <Flex gap={1} flexDirection={"column"} p={3}>
        <SearchInput
          size="sm"
          value={searchTerm}
          onValueChange={value => setSearchTerm(value || '')}
          placeholder={conditionFact === ConditionFact.SPECIFIC_PRODUCT ? t("Search for products") : APP_PLATFORM === Platform.Shopify ? t("Search for product collections") : t("Search for product categories")}
        />
      </Flex>
      <Flex overflowY={"auto"} height={"55vh"} mr={"2px"} flexDirection={"column"}>
        {searching ? (
          <Flex justifyContent={"center"} width={"100%"} alignItems={"center"} height={"100%"}>
            <Spinner />
          </Flex>
        ) : (
          <>
            {products?.length > 0 ? (
              products?.map(({ id, images, title, subTitle, variants }) => {
                let price = variants?.length >= 1 ? variants[0]?.price : "";

                return (
                  <Box key={id}>
                    <Flex
                      my="14px"
                      pl={"24px"}
                      pr="24px"
                      gap="16px"
                      cursor="pointer"
                      onClick={() => {
                        handleProductSelect(
                          id,
                          conditionFact === ConditionFact.SPECIFIC_PRODUCT ? variants?.map(variant => variant.id) : [title],
                          !(selectedProductIdObj[id]?.length >= 0),
                        );
                      }}
                    >
                      <StopPropagation>
                        <Checkbox
                          isChecked={selectedProductIdObj[id]?.length >= 0}
                          onChange={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleProductSelect(
                              id,
                              conditionFact === ConditionFact.SPECIFIC_PRODUCT ? variants?.map(variant => variant.id) : [title],
                              e.target.checked,
                            );
                          }}
                        />
                      </StopPropagation>

                      <Flex alignItems={"center"} flex={1} gap={"12px"}>
                        {conditionFact === "SPECIFIC_PRODUCT" && (
                          <>
                            {images.length > 0 ? (
                              <img src={imageSrc(images[0])} height={"32px"} width={"32px"} alt={title} />
                            ) : (
                              <Icons.ImagePreview w="36px" h="36px" />
                            )}
                          </>
                        )}

                        <Box>
                          <Typography variant="body-16-regular">
                            {title}
                          </Typography>
                          {subTitle && (
                            <Typography mt={0} variant="xsmall">
                              {subTitle}
                            </Typography>
                          )}
                        </Box>
                      </Flex>
                      {conditionFact === "SPECIFIC_PRODUCT" && (
                        <Flex gap={1}>
                          <Typography variant="body" fontSize={"16px"}>
                            {`${currencyFormatter(+price, storeInfo?.currency!)}`}
                          </Typography>
                          <Icons.ChevronRight height={"16px"} width={"16px"} cursor={"pointer"} />
                        </Flex>
                      )}
                    </Flex>
                    <Divider />
                    {/* {variants.length > 1 &&
                  variants.map(variant => (
                    <Flex key={variant.id} my="14px" pl={"56px"} pr="24px" gap="16px">
                      <Checkbox
                        isChecked={productObj[id]?.includes(variant.id)}
                        onChange={e => {
                          handleCheckboxChange(e.target.checked, variant.id, id);
                        }}
                      />
                      <Flex alignItems={"center"} flex={1} gap={"12px"}>
                        <Box>
                          <Typography variant="body-16-regular">
                            {variant.title}
                          </Typography>
                        </Box>
                      </Flex>
                      <Flex gap={1}>
                        <Typography variant="body" fontSize={"16px"}>
                          {variant.price}
                        </Typography>
                      </Flex>
                    </Flex>
                  ))} */}
                  </Box>
                );
              })
            ) : (
              <Flex height={"100%"} alignItems={"center"} justifyContent={"center"} flexDirection={"column"}>
                <Typography variant="body-16-regular">
                  {conditionFact === ConditionFact.SPECIFIC_PRODUCT ? t("There are no product in your store") : t("There are no product collection in your store")}
                </Typography>
                <Typography variant="body-16-regular">
                  {conditionFact === ConditionFact.SPECIFIC_PRODUCT ? t("Please add product to your store") : t("Please add product collection to your store")}

                </Typography>
              </Flex>
            )}
          </>
        )}
      </Flex>
    </>
  );
};

const Footer: React.FC<FooterProps> = ({ onSave, onCancel, t }) => {
  return (
    <Flex gap="8px" p="16px" justifyContent={"end"}>
      <Button
        padding={"8px 16px"}
        variant={"secondary"}
        onClick={onCancel}
      >
        <Typography variant="body-16-regular">
          {t("Cancel")}
        </Typography>
      </Button>

      <Button
        padding={"8px 16px"}
        variant={"solid"}
        onClick={onSave}
      >
        {t("Done")}
      </Button>
    </Flex>
  );
};

const StopPropagation = ({ children }: { children: any }) => {
  const stopEventPropagation = (event: any) => {
    event.stopPropagation();
  };

  return (
    <Flex
      onClick={stopEventPropagation}
      onTouchStart={stopEventPropagation}
      alignItems="center"
      justifyContent="center"
    >
      {children}
    </Flex>
  );
};

export default React.memo(SearchProductModal);
